(() => {
  const media = matchMedia('(max-width:700px)');
  const labels = document.currentScript?.dataset || {};
  const projectTitles = JSON.parse(labels.projectTitles || '{}');
  let cleanups = [];
  let observer;
  let frame;
  const folded = new Map();
  const heights = new Map();

  function fold(parent, nodes, label, after) {
    if (!nodes.length || folded.has(parent)) return;
    const original = [...parent.childNodes];
    const details = document.createElement('details');
    details.className = 'smid-mobile-details';
    const summary = document.createElement('summary');
    summary.textContent = label;
    details.append(summary);
    nodes.forEach(node => details.append(node));
    if (after) after.after(details);
    else parent.append(details);
    folded.set(parent, () => {
      original.forEach(node => parent.append(node));
      details.remove();
    });
  }

  function measureRail(rail) {
    if (!media.matches || rail.closest('#projecten')) return;
    const track = rail.querySelector('[data-rail-track]');
    if (!track) return;
    const bounds = track.getBoundingClientRect();
    if (!bounds.width) return;
    const visible = [...track.children].map(item => {
      const rect = item.getBoundingClientRect();
      return rect;
    }).filter(rect => rect.left < bounds.right - 20 && rect.right > bounds.left + 20);
    if (!visible.length) return;
    const style = getComputedStyle(track);
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const height = Math.ceil(Math.max(...visible.map(rect => rect.height)) + padding);
    if (heights.get(track) === height) return;
    return {track, height};
  }

  function updateHeights() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const heroMeasurements = [...document.querySelectorAll('.hero-slide-dots')].map(dots => ({hero:dots.closest('.hero'), width:dots.getBoundingClientRect().width}));
      const measurements = [...document.querySelectorAll('smid-card-rail')].map(measureRail).filter(Boolean);
      heroMeasurements.forEach(({hero, width}) => {
        if (hero) hero.style.setProperty('--smid-mobile-hero-dots-width', `${width}px`);
      });
      measurements.forEach(({track, height}) => {
        heights.set(track, height);
        track.style.setProperty('--smid-mobile-rail-height', `${height}px`);
        track.dataset.mobileFit = '';
      });
    });
  }

  function reset() {
    cleanups.forEach(cleanup => cleanup());
    cleanups = [];
    observer?.disconnect();
    cancelAnimationFrame(frame);
    folded.forEach(restore => restore());
    folded.clear();
    document.querySelectorAll('[data-mobile-fit]').forEach(track => {
      track.style.removeProperty('--smid-mobile-rail-height');
      delete track.dataset.mobileFit;
    });
    heights.clear();
  }

  function apply() {
    reset();
    if (!media.matches) return;
    document.querySelectorAll('.hero-slide-dots').forEach(dots => {
      const hero = dots.closest('.hero');
      if (!hero) return;
      cleanups.push(() => hero.style.removeProperty('--smid-mobile-hero-dots-width'));
    });
    document.querySelectorAll('#projecten .home-project-card').forEach((card, index) => {
      if (index >= 8) return;
      const original = [...card.childNodes];
      const image = card.querySelector('.home-project-card__image img');
      if (image) {
        const sizes = image.getAttribute('sizes');
        image.setAttribute('sizes', 'calc((100vw - 54px) / 2)');
        cleanups.push(() => {
          if (sizes === null) image.removeAttribute('sizes');
          else image.setAttribute('sizes', sizes);
        });
      }
      const title = card.querySelector(':scope>h3');
      const titleLink = title?.querySelector('a');
      if (titleLink) {
        const url = new URL(titleLink.href);
        const key = url.searchParams.get('view')?.replace(/^smid-/, '') || url.pathname.split('/').filter(Boolean).pop();
        const canonicalMobileTitle = titleLink.dataset.projectMobileTitle;
        const shortTitle = canonicalMobileTitle || projectTitles[key];
        if (shortTitle) {
          const [firstLine, secondLine, location] = shortTitle.split(canonicalMobileTitle ? '~' : '\n');
          const titleNodes = [...titleLink.childNodes];
          const ariaLabel = titleLink.getAttribute('aria-label');
          titleLink.setAttribute('aria-label', titleLink.textContent.trim());
          titleLink.textContent = secondLine ? `${firstLine}\n${secondLine}` : firstLine;
          titleLink.dataset.mobileTitle = '';
          const place = document.createElement('p');
          place.className = 'smid-mobile-project-location';
          place.textContent = location || '';
          if (!canonicalMobileTitle && location) title.after(place);
          cleanups.push(() => {
            titleLink.replaceChildren(...titleNodes);
            delete titleLink.dataset.mobileTitle;
            if (ariaLabel === null) titleLink.removeAttribute('aria-label');
            else titleLink.setAttribute('aria-label', ariaLabel);
            place.remove();
          });
        }
      }
      const copy = [...card.querySelectorAll(':scope>.home-project-card__place,:scope>.home-project-card__body')];
      fold(card, copy, labels.projectDetails, card.querySelector('.smid-mobile-project-location') || title);
      const restoreFold = folded.get(card);
      folded.set(card, () => {
        restoreFold?.();
        card.replaceChildren(...original);
      });
    });
    document.querySelectorAll('#projecten').forEach(section => {
      const track = section.querySelector('[data-rail-track]');
      if (track) {
        const tabindex = track.getAttribute('tabindex');
        const ariaLabel = track.getAttribute('aria-label');
        const labelledby = track.getAttribute('aria-labelledby');
        track.removeAttribute('tabindex');
        track.removeAttribute('aria-label');
        track.setAttribute('aria-labelledby', section.getAttribute('aria-labelledby'));
        cleanups.push(() => {
          if (tabindex === null) track.removeAttribute('tabindex');
          else track.setAttribute('tabindex', tabindex);
          if (ariaLabel === null) track.removeAttribute('aria-label');
          else track.setAttribute('aria-label', ariaLabel);
          if (labelledby === null) track.removeAttribute('aria-labelledby');
          else track.setAttribute('aria-labelledby', labelledby);
        });
      }
      const link = section.querySelector('.section-heading .text-link');
      if (!link) return;
      const allProjects = link.cloneNode(true);
      allProjects.className = 'button button-outline smid-mobile-projects-all';
      section.querySelector('smid-card-rail')?.after(allProjects);
      cleanups.push(() => allProjects.remove());
    });
    document.querySelectorAll('.home-knowledge__body').forEach(body => {
      const title = body.querySelector('h3');
      if (!title) return;
      const label = document.createElement('p');
      label.className = 'smid-mobile-knowledge-label';
      label.textContent = labels.knowledgeLabel;
      title.before(label);
      cleanups.push(() => label.remove());
    });
    document.querySelectorAll('footer.footer-restored').forEach(footer => {
      const grid = footer.querySelector('.footer-grid');
      const brand = grid?.querySelector('.footer-brand');
      const contact = grid?.querySelector('.contact-details');
      if (grid && brand && contact) {
        const original = [...grid.childNodes];
        brand.after(contact);
        cleanups.push(() => original.forEach(node => grid.append(node)));
      }
      const destinations = new Set();
      footer.querySelectorAll('.footer-bottom nav a').forEach(link => {
        const destination = link.href;
        if (destinations.has(destination)) {
          link.classList.add('smid-mobile-footer-duplicate');
          cleanups.push(() => link.classList.remove('smid-mobile-footer-duplicate'));
        } else destinations.add(destination);
      });
    });
    observer = new ResizeObserver(updateHeights);
    document.querySelectorAll('smid-card-rail').forEach(rail => {
      if (rail.closest('#projecten')) return;
      const track = rail.querySelector('[data-rail-track]');
      if (!track) return;
      track.addEventListener('scroll', updateHeights, {passive:true});
      rail.addEventListener('toggle', updateHeights, true);
      cleanups.push(() => {
        track.removeEventListener('scroll', updateHeights);
        rail.removeEventListener('toggle', updateHeights, true);
      });
      observer.observe(track);
      [...track.children].forEach(item => observer.observe(item));
    });
    updateHeights();
  }

  media.addEventListener('change', apply);
  document.addEventListener('shopify:section:load', apply);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, {once:true});
  else apply();
})();
